import { PayPalButtons, PayPalScriptProvider, ReactPayPalScriptOptions } from '@paypal/react-paypal-js';
import { Link } from 'react-router-dom';
import { Interpolated } from '../../../components/Interpolated';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { useCapturePaypalPayment } from '../utils';
import './CheckoutFooter.css';
type PayPalButtonOnApprove = import('@paypal/paypal-js').PayPalButtonOnApprove;
type PayPalButtonCreateOrder = import('@paypal/paypal-js').PayPalButtonCreateOrder;

type CheckoutFooterPaypalProps = {
	submit: () => Promise<void | string>;
	successCallback: () => void;
	errorCallback: (err: any) => void;
	setCreateOrderStatus: React.Dispatch<
		React.SetStateAction<'error' | 'success' | 'loading' | 'cancelled' | undefined>
	>;
	disabled?: boolean;
};

export const CheckoutFooterPaypal = ({
	submit,
	successCallback,
	errorCallback,
	setCreateOrderStatus,
	disabled,
}: CheckoutFooterPaypalProps): JSX.Element => {
	const t = useTranslation().checkout;
	const urls = useTranslation().routes;
	const capturePaypalPayment = useCapturePaypalPayment();
	const { currencyCode, /* country, */ paypalClientId, domain /* locale */ } = useConfig();

	const initialOptions: ReactPayPalScriptOptions = {
		clientId: paypalClientId ?? '',
		'enable-funding': '',
		'disable-funding': 'venmo,credit,card',
		// 'buyer-country': country ?? '',
		currency: currencyCode ?? '',
		'data-page-type': 'product-details',
		components: 'buttons',
		// locale: 'nl_NL',
		intent: 'capture',
		'data-sdk-integration-source': 'developer-studio',
		environment: [
			'www.kontorsgiganten.se',
			'ondio.nl',
			'ondio.de',
			'ondio.at',
			'worky.dk',
			'kontorgiganten.no',
		].includes(domain)
			? 'production'
			: 'sandbox',
		// debug: ['www.kontorsgiganten.se', 'ondio.nl'].includes(domain) ? false : true,
	};

	const style = {
		shape: 'rect',
		layout: 'vertical',
		color: 'black',
		label: 'pay',
		tagline: false,
		height: 50,
		borderRadius: 8,
	} as const;

	const onApprove: PayPalButtonOnApprove = async (data, actions) => {
		if (!data.orderID) {
			throw new Error(`Missing orderID`);
		}
		try {
			const orderData = await capturePaypalPayment(data.orderID);
			const errorDetail = orderData.data?.details?.[0];

			if (errorDetail?.issue === 'INSTRUMENT_DECLINED') {
				return actions.restart();
			} else if (errorDetail) {
				const errorMessage = `${errorDetail.description} (${orderData.data.debug_id})`;

				errorCallback(errorMessage);

				throw new Error(errorMessage);
			} else {
				successCallback();
			}
		} catch (error) {
			console.error(error);
			errorCallback(error);
		}
	};

	const onCreateOrder: PayPalButtonCreateOrder = async (): Promise<string> => {
		const paypalOrderId = await submit();

		if (paypalOrderId === 'INVALID') {
			throw new Error('Form is invalid');
		}

		if (!paypalOrderId) {
			throw new Error('Order creation failed');
		}

		return paypalOrderId;
	};

	return (
		<div className="checkout-footer">
			<p className="policy">
				<Interpolated
					template={t.acceptTerms}
					data={{
						purchaseTerms: <Link to={urls.purchaseTerms}>{t.purchaseTerms}</Link>,
						privacyPolicy: <Link to={urls.privacyPolicy}>{t.privacyPolicy}</Link>,
					}}
				/>
			</p>
			<div className="paypal-wrapper" style={{ minHeight: '50px' }}>
				{disabled ? (
					<div className="disabled-paypal-button">
						<span>{t.payWith}</span>
						<img src="https://image-cdn.content.tctc.se/1767987667970-447031441.svg" />
					</div>
				) : (
					<PayPalScriptProvider options={initialOptions}>
						<PayPalButtons
							disabled={disabled}
							style={style}
							onCancel={() => {
								setCreateOrderStatus('cancelled');
							}}
							onApprove={onApprove}
							onError={(err) => {
								console.error('PayPal Checkout onError', err);
								errorCallback(err);
							}}
							createOrder={onCreateOrder}
						/>
					</PayPalScriptProvider>
				)}
			</div>
		</div>
	);
};
